// React Hooks:
import React, { useState, useEffect } from "react";
// Redux Toolkit Hooks:
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  updateShareManagementCountAction,
  Items,
  shareManagement,
} from "../../shareManagementSlice";
// MUI Components:
import {
  Box,
  SvgIcon,
  Typography,
  Tooltip,
  useTheme,
  Fade,
  TextField,
} from "@mui/material";
// MUI Styles:,
import { makeStyles } from "@mui/styles";
// MUI Icons:
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

// Type Controller
interface EditNumberProps {
  item: Items;
}

const useStyles = makeStyles((theme) => ({
  field: {
    borderRadius: "5px",
    backgroundColor: "#F2F2F2",
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#F2F2F2",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(0),
      "& fieldset": {
        borderColor: "#F2F2F2",
      },
      "&:hover fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#F2F2F2",
        borderWidth: 1.5,
      },
    },
  },
}));

const EditNumber: React.FC<EditNumberProps> = ({ item }) => {
  const classes = useStyles();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { updateShareManagementCount } = useAppSelector(shareManagement);

  const [editable, setEditable] = useState(false);
  const [editNumber, setEditNumber] = useState<number>(+item.number_download);
  
  useEffect(() => {
    if (updateShareManagementCount.alert.message === "updated") {
      setEditable(false);
    }
  }, [updateShareManagementCount.alert.message]);

  return (
    <>
      {editable ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "inherit",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            {item.download}
          </Typography>

          <Typography
            sx={{
              fontSize: "inherit",
              color: "inherit",
              whiteSpace: "nowrap",
              mx: "6px",
              [theme.breakpoints.down("lg")]: {
                mx: "5px",
              },
              [theme.breakpoints.down("md")]: {
                mx: "4px",
              },
            }}
          >
            of
          </Typography>

          <TextField
            name="search"
            placeholder="Search File"
            type="number"
            value={editNumber}
            onChange={(e) => {
              setEditNumber(
                +e.target.value < 0
                  ? 0
                  : +e.target.value >= 1000
                  ? 1000
                  : !!item.alternative
                  ? +e.target.value
                  : item.alternative === "" && +e.target.value <= +item.download
                  ? +item.download
                  : +e.target.value
              );
            }}
            size={"small"}
            sx={{
              label: { color: (theme) => theme.palette.text.secondary },
              input: {
                fontSize: "14px",
                padding: "2px 4px",
                color: (theme) => theme.palette.text.secondary,
                "&::placeholder": {
                  color: (theme) => theme.palette.text.secondary,
                },
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #F2F2F2 inset",
                  WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                  transition: " background-color 600000s 0s, color 600000s 0s",
                },
                [theme.breakpoints.down("lg")]: {
                  fontSize: "12px",
                },
                [theme.breakpoints.down("md")]: {
                  fontSize: "10px",
                },
              },
              width: "70px",
              minWidth: "70px",
              border: "1px solid #dddddd",
              [theme.breakpoints.down("lg")]: {
                minWidth: "55px",
              },
              [theme.breakpoints.down("md")]: {
                minWidth: "50px",
              },
            }}
            className={classes.field}
          />

          <Box
            sx={{
              height: editable === true ? "fit-content" : "0px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Fade in={editable === true} timeout={500}>
              <Tooltip
                arrow
                placement="bottom"
                title="Save"
                enterDelay={1000}
                enterNextDelay={1000}
                leaveDelay={500}
                componentsProps={{
                  tooltip: {
                    sx: {
                      marginTop: "10px !important",
                    },
                  },
                }}
              >
                <SvgIcon
                  component={CheckCircleRoundedIcon}
                  inheritViewBox
                  sx={{
                    width: "auto",
                    height: "20px",
                    color: "#A1A2A3",
                    cursor: "pointer",
                    ml: "6px",
                    [theme.breakpoints.down("lg")]: {
                      height: "18px",
                    },
                    [theme.breakpoints.down("lg")]: {
                      ml: "5px",
                    },
                    [theme.breakpoints.down("md")]: {
                      ml: "4px",
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(
                      updateShareManagementCountAction({
                        new_count: editNumber.toString(),
                        uniq_id: item.uniq_id,
                      })
                    );
                  }}
                />
              </Tooltip>
            </Fade>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "inherit",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            {item.download}
          </Typography>

          <Typography
            sx={{
              fontSize: "inherit",
              color: "inherit",
              whiteSpace: "nowrap",
              mx: "6px",
              [theme.breakpoints.down("lg")]: {
                mx: "5px",
              },
              [theme.breakpoints.down("md")]: {
                mx: "4px",
              },
            }}
          >
            of
          </Typography>

          <Typography
            sx={{
              fontSize: "inherit",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            {item.number_download}
          </Typography>

          <Box
            sx={{
              height: editable === false ? "fit-content" : "0px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Fade in={editable === false} timeout={500}>
              <Tooltip
                arrow
                placement="bottom"
                title="Edit"
                enterDelay={1000}
                enterNextDelay={1000}
                leaveDelay={500}
                componentsProps={{
                  tooltip: {
                    sx: {
                      marginTop: "10px !important",
                    },
                  },
                }}
              >
                <SvgIcon
                  component={EditRoundedIcon}
                  inheritViewBox
                  sx={{
                    width: "auto",
                    height: "20px",
                    color: "#A1A2A3",
                    cursor: "pointer",
                    ml: "6px",
                    [theme.breakpoints.down("lg")]: {
                      height: "18px",
                    },
                    [theme.breakpoints.down("lg")]: {
                      ml: "5px",
                    },
                    [theme.breakpoints.down("md")]: {
                      ml: "4px",
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditable(true);
                  }}
                />
              </Tooltip>
            </Fade>
          </Box>
        </Box>
      )}
    </>
  );
};

export default EditNumber;
